import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Sparkles } from 'lucide-react';
import { LocalStorageService } from '../../../configuration';
import {
  buyLiveBillboard,
  getPublicLiveEffect,
  LiveEffectPublic,
  LiveEffectPublicItem,
  triggerLiveEffect,
} from '../../../api/payment/live-effect.api';
import {
  EFFECT_ICONS,
  formatCa,
  formatCountdown,
  formatSecondsLeft,
  memberEffectPrice,
} from '../../../utils/live-effect';
import { routePaths } from '../../../routes/routes-path';
import { getMembershipStatus } from '../../../api/payment/membership.api';
import JoinMembershipDialog from '../../../components/membership/join-membership-dialog';

const errorMessage = (err: unknown) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data
    ?.message || 'Không gửi được, thử lại sau.';

/** Mục "Hiệu ứng" trên trang streamer: lưới trò bấm được và thẻ gói thầu khung tên. */
const CreatorLiveEffect: React.FC<{
  userId?: string;
  creatorName?: string;
  onAvailableChange?: (available: boolean) => void;
}> = ({ userId, creatorName, onAvailableChange }) => {
  const [data, setData] = useState<LiveEffectPublic | null>(null);
  const [busy, setBusy] = useState(false);
  const [emojiFor, setEmojiFor] = useState<LiveEffectPublicItem | null>(null);
  const [needLogin, setNeedLogin] = useState(false);
  const [hours, setHours] = useState<number | null>(null);
  const [message, setMessage] = useState('');
  const [now, setNow] = useState(Date.now());
  const [isMember, setIsMember] = useState(false);
  // Đã biết có phải hội viên hay chưa (kể cả khi tra lỗi); chưa biết thì chưa cho bấm trò có giá.
  const [memberChecked, setMemberChecked] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const loadedAt = useRef(Date.now());
  const isLogged = !!LocalStorageService.getItem('accessToken');

  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  // Tải lại sau khi gửi mà lỗi thì giữ dữ liệu cũ (không ẩn mục); chỉ bỏ dữ liệu khi đổi sang streamer khác.
  const load = useCallback(() => {
    if (!userId) return;
    getPublicLiveEffect(userId)
      .then((d) => {
        if (userIdRef.current !== userId) return;
        loadedAt.current = Date.now();
        setNow(Date.now());
        setData(d);
      })
      .catch(() => undefined);
  }, [userId]);
  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  // Đếm ngược thời gian chờ mỗi giây (tính từ lúc tải), không gọi lại máy chủ; hết trò nào đang chờ thì dừng.
  const maxCooldownSec = Math.max(
    0,
    ...(data?.effects ?? []).map((e) => e.cooldownLeftSec)
  );
  const ticking =
    maxCooldownSec - Math.floor((now - loadedAt.current) / 1000) > 0;
  useEffect(() => {
    if (!ticking) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [ticking]);

  // Hội viên thấy giá giảm và được dùng trò chỉ dành cho hội viên; chưa đăng nhập hoặc lỗi mạng thì coi như chưa phải.
  const refreshMember = useCallback(() => {
    if (!userId || !isLogged) return;
    getMembershipStatus(userId)
      .then((s) => {
        if (userIdRef.current !== userId) return;
        setIsMember(!!s?.isMember);
        setMemberChecked(true);
      })
      .catch(() => {
        if (userIdRef.current === userId) setMemberChecked(true);
      });
  }, [userId, isLogged]);
  useEffect(() => {
    setIsMember(false);
    setMemberChecked(false);
    refreshMember();
  }, [refreshMember]);

  useEffect(() => {
    if (isLogged) setNeedLogin(false);
  }, [isLogged]);

  const memberLoading = isLogged && !memberChecked;
  const showEffects = !!data?.available && data.effects.length > 0;
  const showBillboard = !!data?.billboard?.enabled;
  const available = !!userId && (showEffects || showBillboard);

  const onAvailableChangeRef = useRef(onAvailableChange);
  onAvailableChangeRef.current = onAvailableChange;
  useEffect(() => {
    onAvailableChangeRef.current?.(available);
    return () => onAvailableChangeRef.current?.(false);
  }, [available]);

  if (!available || !data || !userId) return null;

  const elapsed = Math.floor((now - loadedAt.current) / 1000);
  const cooldownLeft = (e: LiveEffectPublicItem) =>
    Math.max(0, e.cooldownLeftSec - elapsed);

  const priceOf = (e: LiveEffectPublicItem) =>
    memberEffectPrice(
      e.priceCa,
      isMember,
      data.memberDiscountPercent,
      data.minPriceCa ?? 1
    );

  const send = (e: LiveEffectPublicItem, emoji?: string) => {
    if (busy) return;
    if (!isLogged) {
      setNeedLogin(true);
      return;
    }
    const price = priceOf(e);
    if (!window.confirm(`Gửi hiệu ứng ${e.name} với giá ${formatCa(price)}?`))
      return;
    setBusy(true);
    triggerLiveEffect({
      creatorId: userId,
      code: e.code,
      priceCa: price,
      ...(emoji ? { emoji } : {}),
    })
      .then(() => toast.success('Đã gửi, xem trên live nhé!'))
      .catch((err) => toast.error(errorMessage(err)))
      .finally(() => {
        setBusy(false);
        setEmojiFor(null);
        load();
      });
  };

  const onPick = (e: LiveEffectPublicItem) => {
    if (!isLogged) {
      setNeedLogin(true);
      return;
    }
    if (e.membersOnly && !isMember) {
      setJoinOpen(true);
      return;
    }
    if (e.code === 'emoji_rain') setEmojiFor(e);
    else send(e);
  };

  const pkg = data.billboard.packages.find((p) => p.hours === hours) ?? null;
  const buy = () => {
    if (busy || !pkg) return;
    if (!isLogged) {
      setNeedLogin(true);
      return;
    }
    if (
      !window.confirm(
        `Thầu khung ${pkg.hours} giờ với giá ${formatCa(
          pkg.priceCa
        )}? Streamer có thể gỡ nếu nội dung xấu, không hoàn tiền.`
      )
    )
      return;
    setBusy(true);
    buyLiveBillboard({
      creatorId: userId,
      hours: pkg.hours,
      priceCa: pkg.priceCa,
      message: message.trim(),
    })
      .then(() => {
        toast.success('Đã thầu khung, tên bạn sẽ hiện trên live.');
        setMessage('');
      })
      .catch((err) => toast.error(errorMessage(err)))
      .finally(() => {
        setBusy(false);
        load();
      });
  };

  return (
    <div className="mt-8 box rounded-2xl p-4 md:p-6">
      <div className="flex items-center gap-2">
        <Sparkles className="text-blue-600" size={20} />
        <h3 className="text-lg font-semibold ui-heading">Hiệu ứng</h3>
      </div>

      {needLogin && (
        <p className="mt-2 text-[13px] text-amber-700 dark:text-amber-300">
          <Link to={routePaths.login} className="font-semibold underline">
            Đăng nhập để bấm hiệu ứng
          </Link>
        </p>
      )}

      {showEffects && (
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {data.effects.map((e) => {
            const left = cooldownLeft(e);
            const obsOff = e.group === 'Obs' && !data.obsOnline;
            return (
              <button
                key={e.code}
                type="button"
                disabled={busy || left > 0 || obsOff || memberLoading}
                onClick={() => onPick(e)}
                className="flex flex-col items-start rounded-xl border ui-divider p-3 text-left disabled:opacity-50"
              >
                <span className="text-[22px]">{EFFECT_ICONS[e.code]}</span>
                <span className="text-[14px] font-semibold ui-text">
                  {e.name}
                </span>
                <span className="text-[12px] ui-muted">
                  {memberLoading ? 'Đang tải…' : formatCa(priceOf(e))} ·{' '}
                  {e.durationSec} giây
                </span>
                {e.membersOnly && (
                  <span className="text-[12px] font-semibold text-amber-600 dark:text-amber-400">
                    Hội viên
                  </span>
                )}
                {obsOff && (
                  <span className="text-[12px] ui-muted">
                    Streamer chưa bật
                  </span>
                )}
                {!obsOff && left > 0 && (
                  <span className="text-[12px] ui-muted">
                    Bấm lại sau {formatCountdown(left)}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {joinOpen && (
        <JoinMembershipDialog
          creatorId={userId}
          creatorName={creatorName || ''}
          visible={joinOpen}
          onClose={() => {
            setJoinOpen(false);
            // Có thể đã tham gia ở nút trên đầu trang hoặc nơi khác: hỏi lại máy chủ khi đóng.
            refreshMember();
          }}
          onStatus={(s) => {
            setIsMember(!!s?.isMember);
            setMemberChecked(true);
          }}
          onJoined={(s) => {
            setIsMember(!!s?.isMember);
            setJoinOpen(false);
          }}
        />
      )}

      {emojiFor && (
        <div className="mt-3 rounded-lg border ui-divider p-3">
          <p className="text-[13px] ui-text">Chọn emoji sẽ rơi trên live:</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {data.emojis.map((em) => (
              <button
                key={em}
                type="button"
                disabled={busy}
                className="rounded-md border ui-divider px-2 py-1 text-[20px] disabled:opacity-50"
                onClick={() => send(emojiFor, em)}
              >
                {em}
              </button>
            ))}
          </div>
        </div>
      )}

      {showBillboard && (
        <div className="mt-5 rounded-xl border ui-divider p-3">
          <h4 className="font-semibold ui-heading">Gói thầu khung tên</h4>
          {data.billboard.active ? (
            <>
              <p className="mt-1 text-[14px] ui-text">
                📌 {data.billboard.active.viewerName} ·{' '}
                {data.billboard.active.message}
              </p>
              <p className="text-[12px] ui-muted">
                Còn {formatSecondsLeft(data.billboard.active.secondsLeft)} live
              </p>
            </>
          ) : (
            <>
              <p className="mt-1 text-[12px] ui-muted">
                Tên và lời nhắn của bạn hiện suốt trên live. Chỉ tính giờ khi
                streamer đang live. Streamer có thể gỡ nếu nội dung xấu, không
                hoàn tiền.
              </p>
              <div className="mt-2 flex flex-wrap gap-3">
                {data.billboard.packages.map((p) => (
                  <label
                    key={p.hours}
                    className="flex items-center gap-1 text-[13px] ui-text"
                  >
                    <input
                      type="radio"
                      name="billboard-package"
                      checked={hours === p.hours}
                      onChange={() => setHours(p.hours)}
                    />
                    {p.hours} giờ · {formatCa(p.priceCa)}
                  </label>
                ))}
              </div>
              <textarea
                value={message}
                maxLength={100}
                rows={2}
                placeholder="Lời nhắn hiện trên live"
                onChange={(e) => setMessage(e.target.value.slice(0, 100))}
                className="mt-2 w-full rounded-lg border ui-divider bg-transparent p-2 text-[14px] outline-none ui-text"
              />
              <div className="flex items-center justify-between">
                <span className="text-[12px] ui-muted">
                  {message.length}/100
                </span>
                <button
                  type="button"
                  disabled={busy || !pkg || !message.trim()}
                  onClick={buy}
                  className="rounded-lg bg-[#0057FF] px-4 py-1.5 text-[14px] font-semibold text-white disabled:opacity-60"
                >
                  Thầu ngay
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CreatorLiveEffect;
